import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import get from 'lodash/get';
import Tab from '../../../components/Tab';
import AverageGrantAmount from './AverageGrantAmount';
import AveragePellGrantAmount from './AveragePellGrantAmount';
import AverageFederalStudentLoanAmount from './AverageFederalStudentLoanAmount';
import MeritBasedScholarships from './MeritBasedScholarships';
import { getYearFromTimestamp } from '../../../utils/timestamp';
import { getStudentProfileState } from '../../../utils/studentProfiles';
import { isLocationMatch } from '../../../utils/scholarships';
import withHostContext from '../../../context/host/consumer';
import './styles.css';
import Tooltip from '../../../components/Tooltip';
import messages from './messages';
import { PERMISSION_SCHOLARSHIP_MATCH } from '../../../constants';

class FinancialAidSection extends React.PureComponent {
  static propTypes = {
    applicationStatistics: PropTypes.object,
    costsData: PropTypes.object,
    scholarships: PropTypes.object,
    tuitionStatistics: PropTypes.object,
    userData: PropTypes.object,
    testScores: PropTypes.arrayOf(PropTypes.object),
    highSchool: PropTypes.object,
    featureFlags: PropTypes.object,
    permissions: PropTypes.object,
    session: PropTypes.object,
    scid: PropTypes.number,
  };

  constructor(props) {
    super(props);
    this.state = {
      activeIndex: 0,
    };
  }

  handleTabChange = (e, { activeIndex }) => {
    this.setState({
      activeIndex,
    });
  };

  getMenuItem = (message, selected) => {
    return {
      content: this.props.intl.formatMessage(message),
      key: this.props.intl.formatMessage(message),
      role: 'tab',
      'aria-label': this.props.intl.formatMessage(message),
      'aria-selected': selected,
      className: 'financial-aid-tab-item',
      tabIndex: 0,
    };
  };

  getFilteredScholarships = (
    scholarships,
    featureFlags,
    permissions,
    state,
    isHE = false,
  ) => {
    const showScholarship = get(permissions, 'colleges', []).includes(
      PERMISSION_SCHOLARSHIP_MATCH,
    );

    let filteredScholarships = [];
    if (showScholarship && featureFlags.releaseSharedScholarships) {
      const allScholarships = get(scholarships, 'scholarships', []);
      if (allScholarships && allScholarships.length > 0) {
        filteredScholarships = isHE
          ? allScholarships
          : allScholarships.filter((scholarship) => isLocationMatch(state, scholarship));
      }
    }

    return filteredScholarships;
  };

  getPanes = (filteredScholarships) => {
    const {
      costsData,
      scholarships,
      userData,
      tuitionStatistics,
      applicationStatistics,
      testScores,
      highSchool,
      session,
      scid,
    } = this.props;
    const { activeIndex } = this.state;
    const panes = [
      {
        menuItem: this.getMenuItem({ ...messages.avgGrantAmount }, activeIndex === 0),
        render: () => (
          <AverageGrantAmount
            costsData={costsData}
            tuitionStatistics={tuitionStatistics}
          />
        ),
      },
      {
        menuItem: this.getMenuItem({ ...messages.avgPellGrantAmount }, activeIndex === 1),
        render: () => (
          <AveragePellGrantAmount
            costsData={costsData}
            tuitionStatistics={tuitionStatistics}
          />
        ),
      },
      {
        menuItem: this.getMenuItem({ ...messages.avgFedLoanAmount }, activeIndex === 2),
        render: () => (
          <AverageFederalStudentLoanAmount
            costsData={costsData}
            tuitionStatistics={tuitionStatistics}
          />
        ),
      },
    ];

    if (filteredScholarships && filteredScholarships.length > 0) {
      panes.unshift({
        menuItem: this.getMenuItem(
          { ...messages.meritBasedScholarships },
          activeIndex === 0,
        ),
        render: () => (
          <MeritBasedScholarships
            addInfo={get(scholarships, 'addInfo', '')}
            scholarships={filteredScholarships}
            lastUpdated={get(scholarships, 'lastUpdated', '')}
            userData={userData}
            applicationStatistics={applicationStatistics}
            testScores={testScores}
            highSchool={highSchool}
            session={session}
            scid={scid}
          />
        ),
      });
    }
    return panes;
  };

  getUrlLink() {
    const { scholarships } = this.props;
    const { url } = scholarships;

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="financial-aid-urlLink"
      >
        <u>additional scholarships</u>
      </a>
    );
  }

  render() {
    const {
      costsData,
      scholarships,
      featureFlags,
      userData,
      highSchool,
      permissions,
    } = this.props;
    const releaseSharedScholarships = get(
      featureFlags,
      'releaseSharedScholarships',
      false,
    );
    if (releaseSharedScholarships && !scholarships) return null;

    const lastUpdatedYear = getYearFromTimestamp(
      get(costsData, 'lastUpdated.value', null),
    );
    /* istanbul ignore next */
    const footerText = lastUpdatedYear
      ? `Latest available data from ${lastUpdatedYear}`
      : '';

    const isHE = get(this.props, 'hostContext.isHE', false);
    const state = getStudentProfileState(userData, highSchool);
    const filteredScholarships = this.getFilteredScholarships(
      scholarships,
      featureFlags,
      permissions,
      state,
      isHE,
    );
    const showScholarships = releaseSharedScholarships && filteredScholarships.length > 0;

    return (
      <div
        name="financialaid"
        className="financial-aid-section common-full-width"
        data-cy="financial-aid-section"
      >
        <div className="common-core-width">
          <main>
            <aside>
              <h3 tabIndex={0}>
                <FormattedMessage {...messages.title} />
                {showScholarships && (
                  <React.Fragment>
                    <Tooltip
                      popupClassName="financial-aid-title-tooltip"
                      contentClassName="financial-aid-tooltip-container"
                      contentText={<FormattedMessage {...messages.financialAidTooltip} />}
                      showCloseBtn
                    >
                      <i
                        className="info circle icon financial-aid-tooltip-icon"
                        data-cy="financial-aid-tooltip-icon"
                        tabIndex="0"
                        aria-label="Tooltip"
                      />
                    </Tooltip>
                  </React.Fragment>
                )}
              </h3>
              <p tabIndex={0}>
                {showScholarships ? (
                  <FormattedMessage {...messages.info} />
                ) : (
                  <FormattedMessage {...messages.financialAidTooltip} />
                )}
              </p>
              {showScholarships && scholarships.url && (
                <div data-cy="scholarships-links">
                  <FormattedMessage {...messages.infoUrlPrefix} />
                  <p>{this.getUrlLink()}</p>
                  <FormattedMessage {...messages.infoUrlSuffix} />
                </div>
              )}
            </aside>
            <Tab
              className="financial-aid-tabs"
              data-cy="financial-aid-tabs"
              menu={{
                pointing: true,
                role: 'tablist',
              }}
              panes={this.getPanes(filteredScholarships)}
              onTabChange={this.handleTabChange}
              activeIndex={this.state.activeIndex}
            />
          </main>
        </div>
        <footer tabIndex={0}>{footerText}</footer>
      </div>
    );
  }
}

export default injectIntl(withHostContext(FinancialAidSection));
